import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHead, Badge } from "../components/ui-bits";
import { toast } from "sonner";
import { VideoCamera, CalendarPlus, Link as LinkIcon } from "@phosphor-icons/react";

const PLATFORMS = ["Google Meet", "Zoom", "Microsoft Teams"];
const SLOTS = ["10:00 AM", "11:30 AM", "2:00 PM", "4:00 PM", "5:30 PM"];

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [team, setTeam] = useState([]);
  const { user } = useAuth();
  const isAgent = user?.role === "agent";
  const [form, setForm] = useState({ title: "CFO Review", with_expert: isAgent ? "" : "Virtual CFO", platform: "Google Meet", date: "", slot: "4:00 PM" });

  const load = () => api.get("/meetings").then((r) => setMeetings(r.data));
  useEffect(() => {
    load();
    if (isAgent) api.get("/agent/clients").then((r) => setTeam(r.data.map((c) => ({ name: c.company || c.email }))));
    else api.get("/team").then((r) => setTeam(r.data));
  }, []);

  const book = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Please pick a date"); return; }
    if (!form.with_expert) { toast.error("Please pick who the meeting is with"); return; }
    await api.post("/meetings", { ...form, datetime: `${form.date}T${form.slot}` });
    toast.success(`Meeting booked · ${form.platform}`);
    load();
  };

  return (
    <div>
      <PageHead eyebrow={isAgent ? "Book Time With Your Clients" : "Book Time With Your Experts"} title="Meeting Scheduler" />
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={book} className="card p-6 space-y-4 h-fit">
          <p className="label flex items-center gap-2"><CalendarPlus size={16} color="#002FA7" /> New Meeting</p>
          <div>
            <label className="label block mb-1">Title</label>
            <input data-testid="meeting-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div>
            <label className="label block mb-1">With</label>
            <select data-testid="meeting-expert" value={form.with_expert} onChange={(e) => setForm({ ...form, with_expert: e.target.value })}
              className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {isAgent ? <option value="">Select client…</option> : <option>Virtual CFO</option>}
              {team.map((t) => <option key={t.name}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label block mb-1">Platform</label>
            <select data-testid="meeting-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label block mb-1">Date</label>
              <input data-testid="meeting-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </div>
            <div>
              <label className="label block mb-1">Slot</label>
              <select data-testid="meeting-slot" value={form.slot} onChange={(e) => setForm({ ...form, slot: e.target.value })}
                className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                {SLOTS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <button data-testid="meeting-book" className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 rounded-sm font-semibold transition-colors">Book Meeting</button>
        </form>

        <div className="lg:col-span-2">
          <p className="label mb-3">Scheduled Meetings</p>
          <div className="space-y-3">
            {meetings.length === 0 && <p className="text-sm text-[#888] card p-6">No meetings scheduled yet.</p>}
            {meetings.map((m, i) => (
              <div key={i} data-testid={`meeting-${i}`} className="card p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><VideoCamera size={22} color="#002FA7" /></div>
                  <div>
                    <p className="font-head font-bold">{m.title}</p>
                    <p className="text-xs text-[#888]">with {m.with_expert} · {m.platform} · {m.datetime?.replace("T", " · ")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge status="upcoming">{m.status}</Badge>
                  <a data-testid={`meeting-join-${i}`} href={m.link} target="_blank" rel="noopener noreferrer"
                    className="text-sm bg-[#111] text-white px-3 py-2 rounded-sm flex items-center gap-1.5 hover:bg-black transition-colors">
                    <LinkIcon size={15} /> Join
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
